import { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@tanstack/react-router";
import logo from "@/assets/bbn-logo.jpg.asset.json";

interface NavGroup {
  label: string;
  to: string;
  children?: { to: string; label: string }[];
}

const nav: NavGroup[] = [
  { label: "Solfilm företag", to: "/solfilm-foretag" },
  { label: "Solfilm privat", to: "/solfilm-privat" },
  {
    label: "Säkerhetsfilm",
    to: "/sakerhetsfilm",
    children: [
      { to: "/sakerhetsfilm", label: "Översikt" },
      { to: "/sakerhetsfilm-kontor", label: "Kontor" },
      { to: "/sakerhetsfilm-butik", label: "Butik" },
      { to: "/splitterskydd-fonster", label: "Splitterskydd" },
      { to: "/sakerhetsfilm/solar-screen", label: "Solar Screen" },
    ],
  },
  { label: "Dekorfilm", to: "/dekorfilm" },
  { label: "Referenser", to: "/referenser" },
  { label: "Kontakt", to: "/kontakt" },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed left-0 right-0 z-[60] transition-all duration-300 top-0 md:top-9 ${
        scrolled || open
          ? "bg-navy/95 backdrop-blur-lg border-b border-white/10"
          : "bg-navy/30 backdrop-blur-md border-b border-white/5"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" aria-label="BBN Nordic hem">
          <img src={logo.url} alt="BBN Nordic" width={160} height={40} className="h-7 md:h-9 w-auto" />
        </Link>

        <ul className="hidden lg:flex items-center gap-1">
          {nav.map((g) => (
            <li key={g.to} className="relative group">
              <Link
                to={g.to}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-cream/85 hover:text-gold transition-colors whitespace-nowrap"
                activeProps={{ className: "text-gold" }}
              >
                {g.label}
                {g.children && <ChevronDown size={14} className="opacity-60" />}
              </Link>
              {g.children && (
                <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <ul className="bg-navy border border-white/10 rounded shadow-2xl min-w-[220px] py-2">
                    {g.children.map((c) => (
                      <li key={c.to}>
                        <Link
                          to={c.to}
                          className="block px-4 py-2.5 text-sm text-cream/85 hover:bg-white/5 hover:text-gold transition-colors"
                          activeProps={{ className: "text-gold bg-white/5" }}
                        >
                          {c.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
          <li className="ml-2 flex items-center gap-2">
            <Link
              to="/offert"
              className="btn-glow bg-gold text-navy px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-gold-bright transition-colors"
            >
              Få gratis offert →
            </Link>
          </li>

        </ul>

        <div className="lg:hidden flex items-center gap-1">
          <button
            className="min-h-12 min-w-12 flex items-center justify-center text-cream"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Stäng meny" : "Öppna meny"}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden bg-navy/95 backdrop-blur-lg border-t border-white/10 max-h-[calc(100dvh-4rem)] overflow-y-auto">
          <ul className="px-5 py-4 flex flex-col">
            {nav.map((g) => (
              <li key={g.to} className="border-b border-white/10 last:border-0">
                <div className="flex items-center justify-between">
                  <Link
                    to={g.to}
                    onClick={() => setOpen(false)}
                    className="flex-1 py-4 text-base font-medium text-cream/90 hover:text-gold transition-colors"
                  >
                    {g.label}
                  </Link>
                  {g.children && (
                    <button
                      onClick={() => setExpanded(expanded === g.to ? null : g.to)}
                      aria-label="Visa undermeny"
                      className="min-h-12 min-w-12 flex items-center justify-center text-cream/80"
                    >
                      <ChevronDown size={18} className={`transition-transform ${expanded === g.to ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                {g.children && expanded === g.to && (
                  <ul className="pb-3 pl-3">
                    {g.children.map((c) => (
                      <li key={c.to}>
                        <Link
                          to={c.to}
                          onClick={() => setOpen(false)}
                          className="block py-3 text-sm text-cream/70 hover:text-gold"
                        >
                          — {c.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            <li className="pt-4 pb-2">
              <Link
                to="/offert"
                onClick={() => setOpen(false)}
                className="block text-center bg-gold text-navy py-4 rounded-full font-semibold"
              >
                Få gratis offert →
              </Link>
            </li>

          </ul>
        </div>
      )}
    </header>
  );
}
